import { ScheduleGranularity } from './dates';

/**
 * 새 파이프라인의 `start_date` 추천값 — 일배치 D-1, 주배치 D-7, 월배치 D-1month.
 *
 * ## start_date 가 늦으면 당일 테스트를 할 수 없다
 *
 * DAG 은 `catchup: false` 에 `CronDataIntervalTimetable` 이다. 이때 Airflow 는
 * unpause 시점에 **직전 구간 한 건** 을 만들되, 그 구간의 시작이 start_date
 * 이후일 때만 만든다. 그래서 start_date 를 오늘로 두면 일배치(08:30)의 첫 실행은
 * 구간 [오늘 08:30, 내일 08:30] 이 끝나는 **내일 08:30** 이다. 수동 실행도
 * `logical_date` 가 start_date 보다 이르면 태스크가 큐에 들어가지 않는다
 * (`ExecDateAfterStartDateDep`).
 *
 * 주기 하나만큼 당기면 실행 당일(발화 시각 이후)에는 직전 구간이 start_date
 * 안에 들어와 unpause 하자마자 돈다. 발화일이 아닌 날에는 주배치·월배치가 다음
 * 발화까지 기다릴 수 있다.
 *
 * 과거로 당겨도 머지하자마자 돌지는 않는다 — 새 DAG 은 paused 로 만들어진다
 * (`core.dags_are_paused_at_creation=True`, dp-lakehouse dev·prod 실측).
 */
export function recommendedStartDate(
  granularity: ScheduleGranularity,
  now: Date,
): string {
  // UTC 필드를 KST 벽시계로 읽는다. `toISOString()` 을 그대로 쓰면 09:00 KST
  // 전에는 하루 이른 날짜가 된다.
  const kst = new Date(now.getTime() + 9 * 60 * 60 * 1000);
  const y = kst.getUTCFullYear();
  const m = kst.getUTCMonth();
  const d = kst.getUTCDate();
  const date = (() => {
    switch (granularity) {
      case 'monthly': {
        // 3/31 의 한 달 전은 2/28 이다. Date.UTC 에 그대로 넘기면 3/3 이 된다.
        const lastDayOfPrevMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
        return Date.UTC(y, m - 1, Math.min(d, lastDayOfPrevMonth));
      }
      case 'weekly':
        return Date.UTC(y, m, d - 7);
      default:
        return Date.UTC(y, m, d - 1);
    }
  })();
  return new Date(date).toISOString().slice(0, 10);
}

/** 시작일 칸 옆 안내. */
export const START_DATE_GUIDE =
  '주기에 맞춰 자동으로 채워집니다. 기본 설정값 이후의 날짜 선택시 첫 실행이 다음 주기로 밀립니다.';
